import React, { createContext, useContext, useEffect, useState } from 'react';
import api, { testLogin } from '../services/api';
import { canUseLocalTestLogin } from '../utils/devAuth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                const response = await api.get('/auth/status');
                if (response.status === 200 && response.data.authenticated) {
                    setUser(response.data.user);
                    setIsAuthenticated(true);
                } else if (canUseLocalTestLogin()) {
                    const testLoginResponse = await testLogin();
                    setUser(testLoginResponse.data.user);
                    setIsAuthenticated(true);
                } else {
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error('Auth check failed', error);
                setUser(null);
                setIsAuthenticated(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuthStatus();
    }, []);

    const logout = async () => {
        try {
            // Attempt to log out on the backend
            await api.post('/auth/logout');
        } catch (error) {
            // Log the error, but proceed with frontend logout regardless
            console.error('Backend logout failed, but proceeding with frontend logout:', error);
        } finally {
            // By setting isAuthenticated to false, the App component will automatically
            // unmount the calculator and render the LoginPage.
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    const userRole = user?.role || 'user';
    const isAdmin = userRole === 'admin' || userRole === 'superadmin';
    const isSuperadmin = userRole === 'superadmin';
    const isCustomerSuccess = userRole === 'customer_success';
    const profileCompleted = user?.profile_completed === true;
    const needsProfileSetup = isAuthenticated && !profileCompleted && (userRole === 'user' || userRole === 'sales');

    const updateUser = (updatedUser) => {
        setUser(updatedUser);
    };

    const value = {
        user,
        isAuthenticated,
        isLoading,
        logout,
        userRole,
        isAdmin,
        isSuperadmin,
        isCustomerSuccess,
        profileCompleted,
        needsProfileSetup,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
